import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  addSupportComment,
  assignSupportTicket,
  getSupportTicket,
  updateSupportTicket,
} from '@/features/support/api';
import {
  CATEGORY_LABELS,
  EVENT_LABELS,
  STATUS_LABELS,
  isTerminal,
  type SupportComment,
  type SupportEvent,
  type SupportTicketPriority,
  type SupportTicketStatus,
} from '@/features/support/types';
import {
  TicketPriorityBadge,
  TicketStatusBadge,
} from '@/features/support/badges';

import { PageShell } from '@/components/layout/PageShell';
import { ApiErrorCard } from '@/components/feedback/ApiErrorCard';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { useAppSelector } from '@/store/hooks';
import {
  ArrowLeft,
  Clock,
  FileText,
  Lock,
  MessageSquare,
  Phone,
  Send,
  Smartphone,
  User,
} from '@/lib/icons';
import { formatDateTime } from '@/lib/dates';

export const Route = createFileRoute('/_layout/support/$ticketId')({
  component: SupportTicketDetailPage,
});

/** One labelled fact in the context panel. Backlog §6 mandates these fields. */
function ContextRow({
  icon,
  label,
  value,
  mono,
}: Readonly<{
  icon?: React.ReactNode;
  label: string;
  value: string | null | undefined;
  mono?: boolean;
}>) {
  return (
    <div className="flex items-start justify-between gap-4 py-2">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        {icon}
        {label}
      </div>
      <div
        className={`text-right text-sm font-medium text-foreground ${
          mono ? 'font-mono text-xs' : ''
        }`}
      >
        {value || '—'}
      </div>
    </div>
  );
}

function CommentBubble({ comment }: Readonly<{ comment: SupportComment }>) {
  const fromCustomer = comment.authorType === 'customer';

  // Internal notes get a visually distinct treatment on purpose: an operator
  // must be able to tell at a glance what the customer can and cannot see.
  if (comment.isInternal) {
    return (
      <div className="rounded-lg border border-dashed border-[var(--mz-warn-500)] bg-[var(--mz-warn-100)]/40 p-3">
        <div className="mb-1 flex items-center gap-2 text-[11px] font-bold uppercase tracking-wide text-[var(--mz-warn-700)]">
          <Lock className="h-3 w-3" />
          Internal note · not visible to the customer
        </div>
        <div className="whitespace-pre-wrap text-sm text-foreground">
          {comment.body}
        </div>
        <div className="mt-1.5 text-xs text-muted-foreground">
          {comment.authorName ?? 'Operator'} · {formatDateTime(comment.createdAt)}
        </div>
      </div>
    );
  }

  return (
    <div className={`flex ${fromCustomer ? 'justify-start' : 'justify-end'}`}>
      <div
        className={`max-w-[80%] rounded-lg px-3.5 py-2.5 ${
          fromCustomer
            ? 'bg-surface-container-low text-foreground'
            : 'bg-brand-600 text-white'
        }`}
      >
        <div
          className={`mb-1 text-[11px] font-semibold ${
            fromCustomer ? 'text-muted-foreground' : 'text-white/75'
          }`}
        >
          {fromCustomer
            ? (comment.authorName ?? 'Customer')
            : (comment.authorName ?? 'Support')}
        </div>
        <div className="whitespace-pre-wrap text-sm">{comment.body}</div>
        <div
          className={`mt-1 text-[11px] ${
            fromCustomer ? 'text-muted-foreground' : 'text-white/70'
          }`}
        >
          {formatDateTime(comment.createdAt)}
        </div>
      </div>
    </div>
  );
}

function EventRow({ event }: Readonly<{ event: SupportEvent }>) {
  const detail =
    event.eventType === 'status_changed' && event.toValue
      ? (STATUS_LABELS[event.toValue as SupportTicketStatus] ?? event.toValue)
      : event.toValue;

  return (
    <div className="flex gap-3 py-2">
      <div className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-brand-500" />
      <div className="flex-1">
        <div className="text-sm text-foreground">
          {EVENT_LABELS[event.eventType] ?? event.eventType}
          {detail ? <span className="text-muted-foreground"> · {detail}</span> : null}
        </div>
        <div className="text-xs text-muted-foreground">
          {formatDateTime(event.createdAt)}
          {event.actorName ? ` · ${event.actorName}` : ''}
        </div>
      </div>
    </div>
  );
}

function SupportTicketDetailPage() {
  const { ticketId } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const currentUser = useAppSelector((s) => s.auth?.user);

  const [reply, setReply] = useState('');
  const [isInternal, setIsInternal] = useState(false);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['support-ticket', ticketId],
    queryFn: () => getSupportTicket(ticketId),
    staleTime: 10_000,
  });

  const ticket = data?.succeeded ? data.value : undefined;

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['support-ticket', ticketId] });
    queryClient.invalidateQueries({ queryKey: ['support-tickets'] });
    queryClient.invalidateQueries({ queryKey: ['support-ticket-stats'] });
  };

  const commentMutation = useMutation({
    mutationFn: (payload: { body: string; isInternal: boolean }) =>
      addSupportComment(ticketId, payload),
    onSuccess: (result) => {
      // Clear only on success — a failed send must not discard what was typed.
      if (result.succeeded) setReply('');
      invalidate();
    },
  });

  const updateMutation = useMutation({
    mutationFn: (payload: {
      status?: SupportTicketStatus;
      priority?: SupportTicketPriority;
    }) => updateSupportTicket(ticketId, payload),
    onSuccess: invalidate,
  });

  const assignMutation = useMutation({
    mutationFn: (payload: {
      assignedToUserId: string | null;
      assignedToName: string | null;
    }) => assignSupportTicket(ticketId, payload),
    onSuccess: invalidate,
  });

  if (isLoading) {
    return (
      <PageShell title="Support Ticket">
        <div className="space-y-4">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </PageShell>
    );
  }

  if (isError || !ticket) {
    return (
      <PageShell title="Support Ticket">
        <ApiErrorCard
          title="Failed to load ticket"
          error={
            new Error(
              data?.errors?.[0] ?? 'This ticket could not be loaded.'
            )
          }
          onRetry={() => refetch()}
        />
      </PageShell>
    );
  }

  const assignedToMe =
    !!currentUser?.id && ticket.assignedToUserId === currentUser.id;

  const claim = () =>
    assignMutation.mutate({
      assignedToUserId: currentUser?.id ?? null,
      assignedToName:
        [currentUser?.firstName, currentUser?.lastName]
          .filter(Boolean)
          .join(' ') ||
        currentUser?.email ||
        null,
    });

  const sendReply = () => {
    const body = reply.trim();
    if (!body || commentMutation.isPending) return;
    commentMutation.mutate({ body, isInternal });
  };

  return (
    <PageShell
      title={ticket.ticketNumber}
      description={ticket.subject}
      actions={
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate({ to: '/support' })}
        >
          <ArrowLeft className="mr-1.5 h-4 w-4" />
          Back to tickets
        </Button>
      }
    >
      <div className="grid gap-4 lg:grid-cols-3">
        {/* ── Left: conversation + timeline ── */}
        <div className="space-y-4 lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <FileText className="h-4 w-4" />
                Request
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="mb-3 flex flex-wrap items-center gap-2">
                <TicketStatusBadge status={ticket.status} size="default" />
                <TicketPriorityBadge priority={ticket.priority} size="default" />
                <span className="text-sm text-muted-foreground">
                  {CATEGORY_LABELS[ticket.category] ?? ticket.category}
                </span>
              </div>
              <div className="text-sm font-semibold text-foreground">
                {ticket.subject}
              </div>
              {ticket.description ? (
                <p className="mt-2 whitespace-pre-wrap text-sm text-muted-foreground">
                  {ticket.description}
                </p>
              ) : null}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <MessageSquare className="h-4 w-4" />
                Conversation
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {ticket.comments.length === 0 ? (
                <p className="py-4 text-center text-sm text-muted-foreground">
                  No messages yet. Replying here notifies the customer in the app.
                </p>
              ) : (
                ticket.comments.map((comment) => (
                  <CommentBubble key={comment.id} comment={comment} />
                ))
              )}

              {!isTerminal(ticket.status) || ticket.status === 'resolved' ? (
                <div className="space-y-2 border-t border-border-light pt-3">
                  <Textarea
                    value={reply}
                    onChange={(e) => setReply(e.target.value)}
                    placeholder={
                      isInternal
                        ? 'Internal note — the customer will not see this…'
                        : 'Reply to the customer…'
                    }
                    rows={3}
                    maxLength={5000}
                    aria-label={isInternal ? 'Internal note' : 'Reply to customer'}
                  />
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <label className="flex cursor-pointer items-center gap-2 text-sm text-muted-foreground">
                      <input
                        type="checkbox"
                        checked={isInternal}
                        onChange={(e) => setIsInternal(e.target.checked)}
                        className="h-4 w-4 rounded border-border"
                      />
                      <Lock className="h-3.5 w-3.5" />
                      Internal note (not sent to the customer)
                    </label>
                    <Button
                      size="sm"
                      onClick={sendReply}
                      disabled={!reply.trim() || commentMutation.isPending}
                    >
                      <Send className="mr-1.5 h-4 w-4" />
                      {commentMutation.isPending
                        ? 'Sending…'
                        : isInternal
                          ? 'Add note'
                          : 'Send reply'}
                    </Button>
                  </div>
                  {commentMutation.data && !commentMutation.data.succeeded ? (
                    <p className="text-sm text-[var(--mz-error-700)]">
                      {commentMutation.data.errors?.[0] ??
                        'Your message could not be sent.'}
                    </p>
                  ) : null}
                </div>
              ) : (
                <p className="border-t border-border-light pt-3 text-sm text-muted-foreground">
                  This ticket is closed. Reopen it to reply.
                </p>
              )}
            </CardContent>
          </Card>

          {ticket.events.length > 0 ? (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Clock className="h-4 w-4" />
                  Activity
                </CardTitle>
              </CardHeader>
              <CardContent>
                {ticket.events.map((event) => (
                  <EventRow key={event.id} event={event} />
                ))}
              </CardContent>
            </Card>
          ) : null}
        </div>

        {/* ── Right: context + actions ── */}
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <label
                  htmlFor="ticket-status"
                  className="mb-1 block text-xs font-medium text-muted-foreground"
                >
                  Status
                </label>
                <select
                  id="ticket-status"
                  value={ticket.status}
                  onChange={(e) =>
                    updateMutation.mutate({
                      status: e.target.value as SupportTicketStatus,
                    })
                  }
                  disabled={updateMutation.isPending}
                  className="h-9 w-full rounded-md border border-border bg-card px-3 text-sm"
                >
                  {Object.entries(STATUS_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label
                  htmlFor="ticket-priority"
                  className="mb-1 block text-xs font-medium text-muted-foreground"
                >
                  Priority
                </label>
                <select
                  id="ticket-priority"
                  value={ticket.priority}
                  onChange={(e) =>
                    updateMutation.mutate({
                      priority: e.target.value as SupportTicketPriority,
                    })
                  }
                  disabled={updateMutation.isPending}
                  className="h-9 w-full rounded-md border border-border bg-card px-3 text-sm"
                >
                  <option value="low">Low</option>
                  <option value="normal">Normal</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>

              <div className="border-t border-border-light pt-3">
                <div className="mb-1 text-xs font-medium text-muted-foreground">
                  Assignee
                </div>
                <div className="mb-2 text-sm text-foreground">
                  {ticket.assignedToName ?? 'Unassigned'}
                </div>
                {assignedToMe ? (
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full"
                    disabled={assignMutation.isPending}
                    onClick={() =>
                      assignMutation.mutate({
                        assignedToUserId: null,
                        assignedToName: null,
                      })
                    }
                  >
                    Release ticket
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full"
                    disabled={assignMutation.isPending || !currentUser?.id}
                    onClick={claim}
                  >
                    <User className="mr-1.5 h-4 w-4" />
                    Assign to me
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Backlog §6: every field a ticket must carry. */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Customer & Device</CardTitle>
            </CardHeader>
            <CardContent className="divide-y divide-border-light">
              <ContextRow
                icon={<User className="h-3.5 w-3.5" />}
                label="Customer"
                value={ticket.customerName}
              />
              <ContextRow
                icon={<Phone className="h-3.5 w-3.5" />}
                label="MSISDN"
                value={ticket.msisdn}
                mono
              />
              <ContextRow
                icon={<Smartphone className="h-3.5 w-3.5" />}
                label="IMEI"
                value={ticket.imei}
                mono
              />
              <ContextRow label="Device" value={ticket.deviceName} />
              <ContextRow label="Device status" value={ticket.deviceStatus} />
              <ContextRow
                label="Contract"
                value={ticket.contractId ? ticket.contractId.slice(0, 8) : null}
                mono
              />
              <ContextRow label="Raised" value={formatDateTime(ticket.createdAt)} />
              <ContextRow
                label="First response"
                value={formatDateTime(ticket.firstResponseAt)}
              />
              <ContextRow
                label="Resolved"
                value={formatDateTime(ticket.resolvedAt)}
              />
            </CardContent>
          </Card>

          {ticket.contractId ? (
            <Button
              variant="outline"
              className="w-full"
              onClick={() =>
                navigate({
                  to: '/contracts/$contractId',
                  params: { contractId: ticket.contractId! },
                })
              }
            >
              <FileText className="mr-1.5 h-4 w-4" />
              Open contract
            </Button>
          ) : null}
        </div>
      </div>
    </PageShell>
  );
}
